/**
 * 分享社区 —— 帖子流 / 发帖（可附带设备配置快照）/ 点赞 / 评论。
 *
 * 数据隔离：帖子与点赞评论跨账号可见，但每个帖子里的设备是**发帖时冻结的快照**，
 * 读帖不会触达别人的设备；设备墙「一键分享」跳到这里并预选该设备。
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import type { Post, PostComment, PostDeviceSnapshot } from '@znjj/shared';
import { communityApi, postKeys } from '../api/community';
import { deviceKeys, devicesApi } from '../api/devices';
import { Icon } from '../components/Icon';
import { EmptyState, Sheet, fmt, toast } from '../components/ui';

const TABS = [
  { key: 'all', label: '最新' },
  { key: 'mine', label: '我的' },
] as const;

export function CommunityPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [mine, setMine] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [presetDeviceId, setPresetDeviceId] = useState<string | null>(null);
  const [openComments, setOpenComments] = useState<string | null>(null);

  // 设备卡「分享」→ /community?share=<deviceId>：直接打开发帖面板并预选该设备
  const shareId = params.get('share');
  useEffect(() => {
    if (!shareId) return;
    setPresetDeviceId(shareId);
    setComposerOpen(true);
    setParams({}, { replace: true });
  }, [shareId, setParams]);

  const listQuery = useQuery({ queryKey: postKeys.list(mine), queryFn: () => communityApi.list({ mine }) });
  const posts = listQuery.data?.list ?? [];

  const like = useMutation({
    mutationFn: (id: string) => communityApi.like(id),
    onSuccess: (res, id) => {
      // 点赞后同步刷新帖子流与已展开的详情（评论数/点赞数一致）
      void qc.invalidateQueries({ queryKey: ['posts'] });
      void qc.invalidateQueries({ queryKey: postKeys.detail(id) });
      toast.ok(res.liked ? '已点赞' : '已取消点赞');
    },
    onError: (err: Error) => toast.err(err.message),
  });

  return (
    <>
      <div className="page-head">
        <div className="row row--between">
          <div>
            <h1 className="page-title">分享社区</h1>
            <p className="page-sub">
              {listQuery.isPending ? '正在载入帖子…' : `共 ${posts.length} 条帖子 · 点赞与评论跨账号可见`}
            </p>
          </div>
          <button className="btn btn--sm btn--primary" type="button" onClick={() => setComposerOpen(true)}>
            <Icon name="pencil" size={14} />
            发帖
          </button>
        </div>
      </div>

      <div className="row row--wrap" style={{ marginBottom: 12 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            className={'chip' + ((mine ? 'mine' : 'all') === t.key ? ' is-on' : '')}
            type="button"
            onClick={() => setMine(t.key === 'mine')}
          >
            {t.label}
          </button>
        ))}
      </div>

      {listQuery.isPending ? (
        <div className="stack" style={{ gap: 12 }}>
          <div className="skeleton skeleton--card" />
          <div className="skeleton skeleton--card" />
        </div>
      ) : posts.length ? (
        <div className="stack" style={{ gap: 12 }}>
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              commentsOpen={openComments === post.id}
              onLike={() => like.mutate(post.id)}
              onToggleComments={() => setOpenComments((cur) => (cur === post.id ? null : post.id))}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon="users"
          title={mine ? '你还没有发过帖子' : '社区还没有帖子'}
          desc="发帖时可以附带你名下设备的配置快照，其他用户能直接看到这套设备的类型与读数。"
          action={
            <button className="btn btn--sm btn--primary" type="button" onClick={() => setComposerOpen(true)}>
              发第一帖
            </button>
          }
        />
      )}

      <div className="card card--flat" style={{ margin: '16px 0 24px' }}>
        <p className="card__desc">
          帖子里的设备是<strong>发帖瞬间冻结的配置快照</strong>（类型、房间、能力集与当时读数），
          之后你改设备名、开关或删除设备都不会影响历史帖；别人的设备数据不会出现在你的控制台。
        </p>
      </div>

      <ComposerSheet
        key={(composerOpen ? 'open' : 'close') + (presetDeviceId ?? '')}
        open={composerOpen}
        presetDeviceId={presetDeviceId}
        onClose={() => {
          setComposerOpen(false);
          setPresetDeviceId(null);
        }}
      />
    </>
  );
}

/** 单条帖子：作者 / 正文 / 标签 / 设备快照 / 点赞与评论 */
function PostCard({
  post,
  commentsOpen,
  onLike,
  onToggleComments,
}: {
  post: Post;
  commentsOpen: boolean;
  onLike: () => void;
  onToggleComments: () => void;
}) {
  return (
    <article className="card post">
      <div className="row" style={{ gap: 9 }}>
        <span className="avatar">{post.author.nickname.slice(0, 1)}</span>
        <span className="stack" style={{ gap: 1, minWidth: 0 }}>
          <span className="t-sm">
            {post.author.nickname}
            {post.mine ? <span className="t-xs dim"> · 我发布的</span> : null}
          </span>
          <span className="t-xs dim">{fmt.dateTime(post.createdAt)}</span>
        </span>
      </div>

      <h3 className="post__title">{post.title}</h3>
      <p className="post__body">{post.body}</p>

      {post.tags.length ? (
        <div className="row row--wrap" style={{ gap: 6, marginTop: 10 }}>
          {post.tags.map((tag) => (
            <span key={tag} className="post__tag">
              #{tag}
            </span>
          ))}
        </div>
      ) : null}

      {post.devices.length ? (
        <div className="stack" style={{ gap: 6, marginTop: 10 }}>
          {post.devices.map((snap) => (
            <SnapshotRow key={snap.deviceId} snap={snap} />
          ))}
        </div>
      ) : null}

      <div className="post__acts">
        <button
          className={'post__act' + (post.liked ? ' is-on' : '')}
          type="button"
          onClick={onLike}
          aria-pressed={post.liked}
        >
          <Icon name="heart" size={14} />
          <span className="num">{post.likeCount}</span>
        </button>
        <button className="post__act" type="button" onClick={onToggleComments}>
          <Icon name="message-circle" size={14} />
          <span className="num">{post.commentCount}</span>
        </button>
      </div>

      {commentsOpen ? <Comments postId={post.id} /> : null}
    </article>
  );
}

/** 快照设备行：图标 + 名称 + 房间 + 当时读数 + 来源 */
function SnapshotRow({ snap }: { snap: PostDeviceSnapshot }) {
  const cap = snap.spec.capabilities.find((c) => c.access === 'r' && snap.state[c.code] != null);
  const on = snap.state.switch === true;
  return (
    <div className="post__dev">
      <span className="post__dev-ico">
        <Icon name={snap.spec.icon} size={14} />
      </span>
      <span className="post__dev-name">{snap.name}</span>
      {snap.room ? <span className="t-xs dim">{snap.room}</span> : null}
      {snap.spec.capabilities.some((c) => c.code === 'switch') ? (
        <span className={'tag ' + (on ? 'tag--on' : 'tag--plain t-xs dim')}>{on ? '开' : '关'}</span>
      ) : null}
      {cap ? (
        <span className="num t-xs">
          {fmt.num(snap.state[cap.code], cap.type === 'float' ? 1 : 0)}
          {cap.unit ?? ''}
        </span>
      ) : null}
      <span className="t-xs dim">{fmt.source(snap.source)}</span>
    </div>
  );
}

/** 评论列表 + 输入框（详情查询按帖子缓存） */
function Comments({ postId }: { postId: string }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const detail = useQuery({ queryKey: postKeys.detail(postId), queryFn: () => communityApi.detail(postId) });
  const comments: PostComment[] = detail.data?.comments ?? [];

  const add = useMutation({
    mutationFn: () => communityApi.comment(postId, text.trim()),
    onSuccess: () => {
      setText('');
      void qc.invalidateQueries({ queryKey: postKeys.detail(postId) });
      void qc.invalidateQueries({ queryKey: ['posts'] });
    },
    onError: (err: Error) => toast.err(err.message),
  });

  return (
    <div className="cmt">
      {detail.isPending ? (
        <div className="skeleton" style={{ height: 46 }} />
      ) : comments.length ? (
        <div className="cmt__list">
          {comments.map((c) => (
            <div className="cmt__row" key={c.id}>
              <span className="cmt__avatar">{c.author.nickname.slice(0, 1)}</span>
              <span className="stack" style={{ gap: 2, minWidth: 0 }}>
                <span className="t-xs dim">
                  {c.author.nickname}
                  {c.mine ? ' · 我' : ''} · {fmt.dateTime(c.createdAt)}
                </span>
                <span className="t-sm" style={{ lineHeight: 1.6 }}>
                  {c.text}
                </span>
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="t-xs dim" style={{ margin: '2px 0 8px' }}>
          还没有评论，来说点什么
        </p>
      )}

      <div className="cmt__form">
        <input
          className="input"
          value={text}
          maxLength={500}
          placeholder="写下你的评论…"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && text.trim()) add.mutate();
          }}
        />
        <button
          className="btn btn--sm btn--primary"
          type="button"
          disabled={!text.trim() || add.isPending}
          onClick={() => add.mutate()}
        >
          <Icon name="send" size={14} />
        </button>
      </div>
    </div>
  );
}

/** 发帖面板：标题 / 正文 / 标签 / 勾选要附带的设备快照 */
function ComposerSheet({
  open,
  onClose,
  presetDeviceId,
}: {
  open: boolean;
  onClose: () => void;
  presetDeviceId: string | null;
}) {
  const qc = useQueryClient();
  const devicesQuery = useQuery({ queryKey: deviceKeys.list, queryFn: devicesApi.list });
  const devices = devicesQuery.data?.list ?? [];

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [tagText, setTagText] = useState('');
  const [picked, setPicked] = useState<string[]>(presetDeviceId ? [presetDeviceId] : []);

  const save = useMutation({
    mutationFn: () =>
      communityApi.create({
        title: title.trim(),
        body: body.trim(),
        tags: tagText.split(/[,，\s]+/).filter(Boolean),
        deviceIds: picked,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['posts'] });
      toast.ok('已发布到分享社区');
      onClose();
    },
    onError: (err: Error) => toast.err(err.message),
  });

  const toggle = (id: string): void =>
    setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  return (
    <Sheet open={open} onClose={onClose} title="发帖到分享社区">
      <div className="field">
        <span className="field__label">标题</span>
        <input
          className="input"
          value={title}
          maxLength={60}
          placeholder="例如：客厅灯光 + 空调的联动配置"
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="field">
        <span className="field__label">正文</span>
        <textarea
          className="input"
          rows={4}
          value={body}
          maxLength={2000}
          placeholder="说说这套设备怎么用、解决了什么问题…"
          onChange={(e) => setBody(e.target.value)}
        />
      </div>

      <div className="field">
        <span className="field__label">标签（逗号分隔，最多 5 个）</span>
        <input
          className="input"
          value={tagText}
          placeholder="灯光, 联动, ESP32"
          onChange={(e) => setTagText(e.target.value)}
        />
      </div>

      <div className="field">
        <span className="field__label">附带设备配置快照（可选）</span>
        {devicesQuery.isPending ? (
          <div className="skeleton" style={{ height: 40 }} />
        ) : devices.length ? (
          <div className="row row--wrap" style={{ gap: 6 }}>
            {devices.map((d) => (
              <button
                key={d.id}
                className={'chip' + (picked.includes(d.id) ? ' is-on' : '')}
                type="button"
                onClick={() => toggle(d.id)}
              >
                <Icon name={d.spec.icon} size={13} />
                {d.name}
              </button>
            ))}
          </div>
        ) : (
          <span className="field__hint">
            你还没有设备，先去{' '}
            <Link to="/console" style={{ color: 'var(--color-primary)' }}>
              我的设备
            </Link>{' '}
            添加一台。
          </span>
        )}
        <span className="field__hint">
          选中后会把它们的类型、房间、能力集与当前读数冻结成快照一并发布；留空就是纯文字帖。
        </span>
      </div>

      <div className="btn-row" style={{ marginTop: 16 }}>
        <button
          className="btn btn--primary"
          type="button"
          disabled={save.isPending || !title.trim() || !body.trim()}
          onClick={() => save.mutate()}
        >
          <Icon name="send" size={15} />
          {save.isPending ? '发布中…' : '发布'}
        </button>
        <button className="btn btn--ghost" type="button" onClick={onClose}>
          取消
        </button>
      </div>
    </Sheet>
  );
}
